// Venues (spec/14 §3.3, F-HQ-04): every organisation's venues, closed ones included, oldest first
// (`GET /v1/hq/venues`), with the api's filters — name or slug, city, organisation, status, and each
// listing flag — and the flags HQ sets from the list (`PATCH …/listing`).
//
//  - **The flags.** Featured, Partner and Sponsored are HQ's alone: each is a switch that takes effect
//    when pressed, as a switch does, and says so. `publicListing` is the organisation's choice, shown
//    and never set here: a venue that is not publicly listed stays out of the player app whatever its
//    flags say (F-ORG-01 rule 6). Status and hours are the organisation's too, read here only.
//  - **The read is audited** in every organisation whose venues are on the page, so the page says so.
//  - **Not yet in the row:** stations, client versions, online state and photos awaiting moderation
//    join it with the features that own them (F-STN-02, F-HQ-32, F-HQ-34, photo moderation).
//
// `hqAccess` narrows nothing here (the product owner, 2026-09-22): a venue is what an organisation
// publishes, not its customer data.

import type { HqVenue, Venue } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Chip, FormSection, InfoTip, PageSkeleton, SelectField, Switch, Table, TextField, VisuallyHidden, type TableColumn } from '@clavian/ui-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Fragment, useCallback, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { problemCode, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Dot } from '../../ui';
import { NotPermitted, RequirePlatformPermission } from '../access';
import { organizationParams, useOrganizationList } from '../organizations/queries';
import { HQ_PATHS } from '../paths';
import { HQ_ORGANIZATIONS } from '../sections';
import {
  HQ_FLAGS,
  LISTING_FILTERS,
  replaceVenue,
  useVenueList,
  VENUE_STATUSES,
  venueFiltersFrom,
  type HqFlag,
  type VenueFilters,
  type VenueStatus,
} from './queries';

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export function VenuesPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_ORGANIZATIONS}>
      <VenueDirectory />
    </RequirePlatformPermission>
  );
}

function VenueDirectory() {
  const { t } = useTranslation('hq');
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const filters = useMemo(() => venueFiltersFrom(search), [search]);
  const list = useVenueList(filters);
  const [announcement, setAnnouncement] = useState<{ readonly text: string; readonly failed: boolean } | undefined>(undefined);
  const announce = useCallback((text: string, failed: boolean) => setAnnouncement({ text, failed }), []);

  const header = (
    <div className="console-hq-titlebar">
      <div>
        <div className="console-hq-heading">
          <h1 className="console-page__title">{t('venues.title')}</h1>
          <InfoTip>
            {t('venues.intro')} {t('venues.audited')}
          </InfoTip>
        </div>
      </div>
    </div>
  );

  let body: ReactNode;
  if (list.data === undefined) {
    if (list.isError && problemCode(list.error) === PROBLEM.forbidden) return <NotPermitted />;
    body = list.isError ? (
      <LoadFailure error={list.error} message={t('venues.unavailable')} onRetry={() => void list.refetch()} />
    ) : (
      <PageSkeleton variant="list" />
    );
  } else {
    const rows = list.data.pages.flatMap((page) => page.data);
    body = (
      <>
        {/* Always there, so what it says when a flag is set is announced. */}
        <p role="status" className="console-muted">
          {announcement !== undefined && !announcement.failed ? announcement.text : ''}
        </p>
        {announcement?.failed ? (
          <p className="console-alert" role="alert">
            {announcement.text}
          </p>
        ) : null}
        <VenueTable rows={rows} onAnnounce={announce} />
        {list.hasNextPage ? (
          <Button className="console-hq-more" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {t('venues.more')}
          </Button>
        ) : null}
        {list.isFetchNextPageError ? <LoadFailure error={list.error} onRetry={() => void list.fetchNextPage()} /> : null}
      </>
    );
  }

  return (
    <div className="console-page">
      {header}
      {/* Keyed by the address's filters: going back to another filter is another form, never this one's draft. */}
      <FilterForm key={JSON.stringify(filters)} filters={filters} />
      {body}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The filters
// ---------------------------------------------------------------------------------------------

type Tri = 'any' | 'true' | 'false';

interface Draft {
  q: string;
  city: string;
  organizationId: string;
  status: readonly VenueStatus[];
  publicListing: Tri;
  featured: Tri;
  partner: Tri;
  sponsored: Tri;
}

const EVERY = 'all';

function draftOf(filters: VenueFilters): Draft {
  return {
    q: filters.q ?? '',
    city: filters.city ?? '',
    organizationId: filters.organizationId ?? EVERY,
    status: (filters.status ?? '').split(',').filter((status): status is VenueStatus => (VENUE_STATUSES as readonly string[]).includes(status)),
    publicListing: filters.publicListing ?? 'any',
    featured: filters.featured ?? 'any',
    partner: filters.partner ?? 'any',
    sponsored: filters.sponsored ?? 'any',
  };
}

function searchOf(draft: Draft): Record<string, string> {
  const search: Record<string, string> = {};
  if (draft.q.trim() !== '') search['q'] = draft.q.trim();
  if (draft.city.trim() !== '') search['city'] = draft.city.trim();
  if (draft.organizationId !== EVERY) search['organizationId'] = draft.organizationId;
  if (draft.status.length > 0) search['status'] = VENUE_STATUSES.filter((status) => draft.status.includes(status)).join(',');
  for (const flag of LISTING_FILTERS) {
    if (draft[flag] !== 'any') search[flag] = draft[flag];
  }
  return search;
}

function FilterForm({ filters }: { readonly filters: VenueFilters }) {
  const { t } = useTranslation('hq');
  const { t: tc } = useTranslation('console');
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft>(() => draftOf(filters));
  const [qError, setQError] = useState<string | undefined>(undefined);
  const qRef = useRef<HTMLDivElement>(null);
  const organizations = useOrganizationList();

  const organizationOptions = useMemo(() => {
    const known = (organizations.data?.pages ?? []).flatMap((page) => page.data).map((organization) => ({ value: organization.id, label: organization.name }));
    const current = filters.organizationId;
    const withCurrent = current === undefined || known.some((option) => option.value === current) ? known : [{ value: current, label: current }, ...known];
    return [{ value: EVERY, label: t('venues.everyOrganization') }, ...withCurrent];
  }, [organizations.data, filters.organizationId, t]);

  const triOptions = [
    { value: 'any', label: t('venues.any') },
    { value: 'true', label: t('venues.yes') },
    { value: 'false', label: t('venues.no') },
  ];

  const apply = (event: FormEvent) => {
    event.preventDefault();
    // The api takes two letters or more; one would be refused, not ignored.
    if (draft.q.trim().length === 1) {
      setQError(t('venues.qTooShort'));
      qRef.current?.querySelector('input')?.focus();
      return;
    }
    setQError(undefined);
    void navigate({ to: HQ_PATHS.venues, search: searchOf(draft) });
  };

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((previous) => ({ ...previous, [key]: value }));

  // Search, organisation and status are the essentials; the city and the four listing flags sit behind "More options",
  // open from the start when one of them is already in the address, so a filter in force is never hidden.
  const moreActive = filters.city !== undefined || LISTING_FILTERS.some((flag) => filters[flag] !== undefined);

  return (
    <form className="console-hq-filters" role="search" aria-label={t('venues.filters')} noValidate onSubmit={apply}>
      <FormSection
        defaultOpen={moreActive}
        more={
          <div className="console-hq-filters__fields">
            <TextField label={t('venues.field.city')} value={draft.city} autoComplete="off" onChange={(event) => set('city', event.target.value)} />
            {LISTING_FILTERS.map((flag) => (
              <SelectField
                key={flag}
                label={t(`venues.field.${flag}`)}
                options={triOptions}
                value={draft[flag]}
                onValueChange={(value) => set(flag, value === 'true' || value === 'false' ? value : 'any')}
              />
            ))}
          </div>
        }
      >
        <div className="console-hq-filters__fields">
          <div ref={qRef}>
            <TextField
              label={t('venues.field.q')}
              value={draft.q}
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => set('q', event.target.value)}
              {...(qError === undefined ? {} : { error: qError })}
            />
          </div>
          <SelectField
            label={t('venues.field.organization')}
            options={organizationOptions}
            value={draft.organizationId}
            onValueChange={(value) => set('organizationId', value)}
          />
        </div>
        <div className="console-hq-chips" role="group" aria-label={t('venues.field.status')}>
          {VENUE_STATUSES.map((status) => (
            <Chip
              key={status}
              selected={draft.status.includes(status)}
              onSelectedChange={(selected) => set('status', selected ? [...draft.status, status] : draft.status.filter((known) => known !== status))}
            >
              {tc(`venueProfile.status.${status}`)}
            </Chip>
          ))}
        </div>
      </FormSection>
      <div className="console-form__actions">
        <Button type="submit" variant="primary">
          {t('venues.apply')}
        </Button>
        <Button variant="ghost" onClick={() => void navigate({ to: HQ_PATHS.venues, search: {} })}>
          {t('venues.clear')}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
// The list, and the flags
// ---------------------------------------------------------------------------------------------

function VenueTable({ rows, onAnnounce }: { readonly rows: readonly HqVenue[]; readonly onAnnounce: (text: string, failed: boolean) => void }) {
  const { t } = useTranslation('hq');
  const { t: tc } = useTranslation('console');

  const columns = useMemo<readonly TableColumn<HqVenue>[]>(
    () => [
      {
        key: 'venue',
        header: t('venues.column.venue'),
        cell: ({ venue }) => (
          <>
            {venue.name}
            <span className="console-hq-secondary">{venue.slug}</span>
          </>
        ),
      },
      {
        key: 'organization',
        header: t('venues.column.organization'),
        cell: ({ organization }) => (
          <Link to={HQ_PATHS.organization} params={organizationParams(organization.id)}>
            {organization.name}
          </Link>
        ),
      },
      {
        key: 'location',
        header: t('venues.column.location'),
        cell: ({ venue }) =>
          venue.address === null ? (
            <span className="console-muted">{t('venues.noAddress')}</span>
          ) : (
            <>
              {venue.address.locality}
              <span className="console-hq-secondary">{venue.address.region}</span>
            </>
          ),
      },
      { key: 'hours', header: t('venues.column.hours'), cell: ({ venue }) => <Hours venue={venue} /> },
      {
        key: 'status',
        header: t('venues.column.status'),
        cell: ({ venue }) => (
          <Dot tone={venue.status === 'live' ? 'ok' : venue.status === 'paused' ? 'warn' : venue.status === 'closed' ? 'danger' : 'neutral'}>{tc(`venueProfile.status.${venue.status}`)}</Dot>
        ),
      },
      {
        key: 'publicListing',
        header: t('venues.column.publicListing'),
        cell: ({ venue }) => (venue.publicListing ? t('venues.listed') : <span className="console-muted">{t('venues.notListed')}</span>),
      },
      {
        key: 'flags',
        header: t('venues.column.flags'),
        cell: (row) => (
          <div className="console-hq-flags">
            {HQ_FLAGS.map((flag) => (
              <FlagSwitch key={flag} row={row} flag={flag} onAnnounce={onAnnounce} />
            ))}
          </div>
        ),
      },
    ],
    [t, tc, onAnnounce],
  );

  return (
    // A venue's state as the rail at its row's edge (ADR-0043): paused wants a look, closed is over.
    <Table
      caption={t('venues.caption')}
      captionHidden
      columns={columns}
      rows={rows}
      rowKey={(row) => row.venue.id}
      rowTone={(row) => (row.venue.status === 'paused' ? 'warn' : row.venue.status === 'closed' ? 'quiet' : undefined)}
      empty={t('venues.empty')}
    />
  );
}

/** A venue's week, folded under a summary: the organisation's hours, read only here. */
function Hours({ venue }: { readonly venue: Venue }) {
  const { t } = useTranslation('hq');
  const { t: tc } = useTranslation('console');
  const range = (opens: string, closes: string) => {
    if (opens === closes) return tc('settings.hours.allDay');
    return closes < opens ? `${opens}–${closes} ${tc('settings.hours.nextDay')}` : `${opens}–${closes}`;
  };
  return (
    <details className="console-hq-hours">
      <summary>
        <Named visible={t('venues.hours')} full={t('venues.hoursFor', { venue: venue.name })} />
      </summary>
      <dl>
        {DAYS.map((day) => {
          const intervals = venue.openingHours[day];
          return (
            <Fragment key={day}>
              <dt>{tc(`venueProfile.label.${day}`)}</dt>
              <dd>
                {intervals.length === 0 ? tc('settings.hours.closed') : intervals.map((interval) => range(interval.opens, interval.closes)).join(', ')}
              </dd>
            </Fragment>
          );
        })}
      </dl>
    </details>
  );
}

/**
 * One of HQ's flags on one venue. It moves the moment it is pressed and is sent at once; if the api
 * refuses, it moves back and the page says why. A press while one is on its way is ignored.
 */
function FlagSwitch({ row, flag, onAnnounce }: { readonly row: HqVenue; readonly flag: HqFlag; readonly onAnnounce: (text: string, failed: boolean) => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const api = useApi();
  const queryClient = useQueryClient();
  const { venue, organization } = row;
  const words = { flag: t(`venues.field.${flag}`), venue: venue.name };

  const save = useMutation({
    mutationFn: (value: boolean) =>
      api.updateVenueListing({ path: { organizationId: organization.id, venueId: venue.id }, body: { [flag]: value } }),
    onMutate: (value) => replaceVenue(queryClient, (known) => (known.id === venue.id ? { ...known, [flag]: value } : undefined)),
    onSuccess: (saved, value) => {
      replaceVenue(queryClient, (known) => (known.id === saved.id ? saved : undefined));
      onAnnounce(t(value ? 'venues.flagOn' : 'venues.flagOff', words), false);
    },
    onError: (error, value) => {
      replaceVenue(queryClient, (known) => (known.id === venue.id ? { ...known, [flag]: !value } : undefined));
      const reason =
        problemCode(error) === PROBLEM.forbidden
          ? t('venues.flagForbidden')
          : problemCode(error) === PROBLEM.notFound
            ? t('venues.flagGone')
            : problems.text(error);
      onAnnounce(`${t('venues.flagFailed', words)} ${reason}`, true);
    },
  });

  return (
    <Switch
      label={<Named visible={words.flag} full={t('venues.flagFor', words)} />}
      checked={venue[flag]}
      onCheckedChange={(checked) => {
        if (!save.isPending) save.mutate(checked);
      }}
    />
  );
}

/**
 * A row's control, seen as its word and heard with its venue: "Partner", and "Partner, Nitro FC Road".
 * The full name alone is read — the visible word begins it (WCAG 2.5.3) — so every environment
 * computes the same name, whatever spacing it puts around hidden text.
 */
function Named({ visible, full }: { readonly visible: string; readonly full: string }) {
  return (
    <>
      <span aria-hidden="true">{visible}</span>
      <VisuallyHidden>{full}</VisuallyHidden>
    </>
  );
}
